<template>
    <v-container class="rewards-container mb-12 mb-lg-0">
        <v-row justify="center">
            <v-col cols="12" lg="10" class="rewards-main-col">
                <v-card width="100%" class="rewards-shell">
                    <v-card-title class="d-block text-center font-weight-bold primary secondary--text pa-1" style="word-break: normal">
                        {{ $vuetify.breakpoint.xsOnly ? 'RECOMPENSAS' : 'SORTEOS Y RECOMPENSAS' }}
                    </v-card-title>
                    <v-btn bottom color="secondary primary--text" depressed fab absolute right small @click="getData">
                        <v-icon>mdi-sync</v-icon>
                    </v-btn>
                    <v-card-text class="rewards-body pa-4">
                        <v-progress-linear indeterminate v-if="loading"/>
                        <template v-else>
                            <v-alert dense text type="warning" v-if="!rewards.enabled">
                                Las recompensas aun no estan activas.
                            </v-alert>
                            <v-row class="reward-stats-row" dense>
                                <v-col cols="4" md="4">
                                    <v-card outlined class="reward-stat-card">
                                        <v-card-text class="text-center">
                                            <v-icon color="primary" :size="$vuetify.breakpoint.xsOnly ? 28 : 44">mdi-hand-coin</v-icon>
                                            <div class="reward-stat-label grey--text text--darken-1 mt-2">Puntos</div>
                                            <div class="reward-stat-value font-weight-bold primary--text">{{ rewards.points_balance }}</div>
                                        </v-card-text>
                                    </v-card>
                                </v-col>
                                <v-col cols="4" md="4">
                                    <v-card outlined class="reward-stat-card">
                                        <v-card-text class="text-center">
                                            <v-icon color="success" :size="$vuetify.breakpoint.xsOnly ? 28 : 44">mdi-gift-open</v-icon>
                                            <div class="reward-stat-label grey--text text--darken-1 mt-2">Canje</div>
                                            <div class="reward-stat-value font-weight-bold success--text">Beneficios</div>
                                        </v-card-text>
                                    </v-card>
                                </v-col>
                                <v-col cols="4" md="4">
                                    <v-card outlined class="reward-stat-card">
                                        <v-card-text class="text-center">
                                            <v-icon color="info" :size="$vuetify.breakpoint.xsOnly ? 28 : 44">mdi-play-circle</v-icon>
                                            <div class="reward-stat-label grey--text text--darken-1 mt-2">Videos</div>
                                            <div class="reward-stat-value font-weight-bold info--text">
                                                {{ rewards.daily_views }}<span v-if="rewards.max_daily_rewards">/{{ rewards.max_daily_rewards }}</span>
                                            </div>
                                            <div class="reward-stat-subtitle grey--text text--darken-1" v-if="rewards.max_hourly_rewards">
                                                Hora: {{ rewards.hourly_views }}/{{ rewards.max_hourly_rewards }}
                                            </div>
                                        </v-card-text>
                                    </v-card>
                                </v-col>
                            </v-row>
                            <v-alert dense text type="info" class="mt-4">
                                {{ rewards.reward_policy_notice }}
                            </v-alert>
                            <v-alert dense text type="success" class="mt-4">
                                Cada video completado puede sumar hasta {{ rewards.points_per_estimated_video }} puntos internos,
                                segun la configuracion vigente de recompensas.
                            </v-alert>
                            <v-card outlined class="reward-progress-card mt-4">
                                <v-card-text>
                                    <div class="d-flex align-center mb-2">
                                        <v-icon color="primary" class="mr-2">mdi-progress-check</v-icon>
                                        <strong>Meta de canje: {{ rewards.min_redeem_points }} puntos</strong>
                                    </div>
                                    <v-progress-linear :value="redeemProgress" color="primary" height="12" rounded/>
                                    <div class="caption grey--text text--darken-1 mt-2">
                                        Los puntos no son transferibles y se usaran para beneficios disponibles dentro de Valzum.
                                    </div>
                                    <v-alert dense text type="warning" class="mt-3 mb-0" v-if="!rewards.cash_redemption_enabled">
                                        El canje directo a dinero, cripto o saldo wallet no esta disponible para recompensas de videos.
                                    </v-alert>
                                    <v-alert dense text type="info" class="mt-3 mb-0" v-if="rewards.redemption_options.products_enabled">
                                        Tu pais tiene canje por productos habilitado. Proximamente podras elegir productos disponibles.
                                    </v-alert>
                                    <v-btn
                                        v-if="rewards.cash_redemption_enabled"
                                        block
                                        large
                                        color="success"
                                        class="mt-4"
                                        :loading="redeeming"
                                        :disabled="redeeming || !rewards.can_redeem"
                                        @click="redeem">
                                        <v-icon>mdi-wallet-plus</v-icon>&nbsp;&nbsp;Canjear a wallet
                                    </v-btn>
                                    <v-btn v-else block large color="primary" outlined class="mt-4" disabled>
                                        <v-icon>mdi-gift-outline</v-icon>&nbsp;&nbsp;Catalogo de beneficios en preparacion
                                    </v-btn>
                                </v-card-text>
                            </v-card>
                            <v-card outlined class="reward-action-card mt-4" v-if="rewards.subscription.enabled">
                                <v-card-text>
                                    <div class="d-flex flex-column flex-md-row align-md-center">
                                        <div class="flex-grow-1">
                                            <div class="subtitle-1 font-weight-bold">{{ rewards.subscription.plan_name }}</div>
                                            <div class="body-2 grey--text text--darken-1" v-if="rewards.subscription.active">
                                                Activa hasta {{ rewards.subscription.expires_at }}.
                                            </div>
                                            <div class="body-2 grey--text text--darken-1" v-else>
                                                {{ rewards.subscription.price }} por {{ rewards.subscription.duration_days }} dias.
                                                Limite: {{ rewards.subscription.daily_limit }} videos al dia y {{ rewards.subscription.hourly_limit }} por hora.
                                            </div>
                                        </div>
                                        <v-btn
                                            color="secondary"
                                            class="primary--text mt-3 mt-md-0"
                                            :block="$vuetify.breakpoint.xsOnly"
                                            :loading="subscribing"
                                            :disabled="subscribing || rewards.subscription.active"
                                            @click="subscribe">
                                            <v-icon>mdi-crown</v-icon>&nbsp;&nbsp;Activar
                                        </v-btn>
                                    </div>
                                </v-card-text>
                            </v-card>
                            <v-card outlined class="reward-action-card mt-4">
                                <v-card-text>
                                    <div class="d-flex flex-column flex-md-row align-md-center">
                                        <div class="flex-grow-1">
                                            <div class="subtitle-1 font-weight-bold">Videos recompensados</div>
                                            <div class="body-2 grey--text text--darken-1">{{ videoStatusText }}</div>
                                        </div>
                                        <v-btn
                                            color="primary"
                                            large
                                            class="mt-3 mt-md-0"
                                            :block="$vuetify.breakpoint.xsOnly"
                                            :loading="watching"
                                            :disabled="watching || !canRequestRewardVideo"
                                            @click="watchVideo">
                                            <v-icon>mdi-play-circle</v-icon>&nbsp;&nbsp;Ver video
                                        </v-btn>
                                    </div>
                                    <v-alert dense text type="info" class="mt-3 mb-0" v-if="!nativeRewardBridgeAvailable">
                                        Abre esta seccion desde la app movil para ver videos recompensados.
                                    </v-alert>
                                </v-card-text>
                            </v-card>
                            <v-card outlined class="reward-history-card mt-4">
                                <v-card-title class="subtitle-1 font-weight-bold">Ultimas recompensas</v-card-title>
                                <v-data-table
                                    :headers="viewHeaders"
                                    :items="rewards.views"
                                    dense
                                    :hide-default-footer="$vuetify.breakpoint.xsOnly"
                                    no-data-text="Aun no tienes videos recompensados"
                                    :footer-props="footerProps"/>
                            </v-card>
                            <v-card outlined class="reward-history-card mt-4">
                                <v-card-title class="subtitle-1 font-weight-bold">Canjes</v-card-title>
                                <v-data-table
                                    :headers="redemptionHeaders"
                                    :items="rewards.redemptions"
                                    dense
                                    :hide-default-footer="$vuetify.breakpoint.xsOnly"
                                    no-data-text="Aun no tienes canjes"
                                    :footer-props="footerProps"/>
                            </v-card>
                        </template>
                    </v-card-text>
                </v-card>
            </v-col>
        </v-row>
    </v-container>
</template>

<script>
import axios from 'axios';

export default {
    name: "Rewards",
    props: [ 'user' ],
    data: () => ({
        loading: true,
        redeeming: false,
        watching: false,
        subscribing: false,
        rewards: {
            enabled: false,
            points_balance: '0',
            raw_points_balance: 0,
            money_available: '\u20AE 0,00 USDT',
            cash_redemption_enabled: false,
            reward_policy_notice: 'Los puntos de videos son internos, no transferibles y no se convierten directo a dinero, cripto ni saldo wallet.',
            min_redeem_points: '0',
            raw_min_redeem_points: 0,
            point_value_usdt: 0,
            customer_share_percent: 50,
            customer_value_per_video_usdt: 0,
            points_per_estimated_video: '0',
            can_redeem: false,
            daily_views: 0,
            hourly_views: 0,
            max_daily_rewards: 0,
            max_hourly_rewards: 0,
            daily_remaining: null,
            hourly_remaining: null,
            next_available_at: null,
            redemption_options: {
                usdt_enabled: true,
                products_enabled: false
            },
            subscription: {
                enabled: false,
                active: false,
                plan_name: 'Recompensas Plus',
                price: '\u20AE 0,00 USDT',
                raw_price: 0,
                duration_days: 30,
                daily_limit: 0,
                hourly_limit: 0,
                expires_at: null
            },
            admob: {
                ssv_user_id: '',
                ssv_custom_data: '',
                android_ad_unit_id: '',
                ios_ad_unit_id: '',
                reward_item: 'points',
                ssv_callback_url: ''
            },
            views: [],
            redemptions: []
        },
        viewHeaders: [
            { text: 'Fecha', value: 'watched_at' },
            { text: 'Puntos', value: 'points', align: 'right' },
            { text: 'Estado', value: 'status', align: 'center' },
        ],
        redemptionHeaders: [
            { text: 'Fecha', value: 'created_at' },
            { text: 'Puntos', value: 'points', align: 'right' },
            { text: 'Monto', value: 'amount_usdt', align: 'right' },
            { text: 'Estado', value: 'status', align: 'center' },
        ],
        footerProps: {
            itemsPerPageOptions: [10, 25, 50]
        }
    }),
    computed: {
        redeemProgress() {
            const min = Number(this.rewards.raw_min_redeem_points || 0);
            if (!min) return 0;

            return Math.min(100, Math.round((Number(this.rewards.raw_points_balance || 0) / min) * 100));
        },
        nativeRewardBridgeAvailable() {
            if (typeof window === 'undefined') return false;

            return !!(
                window.ValzumRewards && typeof window.ValzumRewards.showRewardedAd === 'function'
            ) || !!(
                window.webkit
                && window.webkit.messageHandlers
                && window.webkit.messageHandlers.valzumRewards
            );
        },
        hasRewardCooldown() {
            if (!this.rewards.next_available_at) return false;

            const next = new Date(String(this.rewards.next_available_at).replace(' ', 'T')).getTime();

            return next && next > Date.now();
        },
        hasRewardAdUnit() {
            const admob = this.rewards.admob || {};

            return !!(admob.android_ad_unit_id || admob.ios_ad_unit_id);
        },
        canRequestRewardVideo() {
            if (!this.rewards.enabled || !this.hasRewardAdUnit || this.hasRewardCooldown) return false;
            if (this.rewards.hourly_remaining !== null && Number(this.rewards.hourly_remaining) <= 0) return false;

            return this.rewards.daily_remaining === null || Number(this.rewards.daily_remaining) > 0;
        },
        videoStatusText() {
            if (!this.rewards.enabled) return 'Las recompensas aun no estan activas.';
            if (!this.hasRewardAdUnit) return 'Falta configurar el Ad Unit ID de AdMob.';
            if (this.rewards.daily_remaining !== null && Number(this.rewards.daily_remaining) <= 0) {
                return 'Ya alcanzaste el limite diario de videos recompensados.';
            }
            if (this.rewards.hourly_remaining !== null && Number(this.rewards.hourly_remaining) <= 0) {
                return 'Ya alcanzaste el limite de videos por hora.';
            }
            if (this.hasRewardCooldown) return 'Espera unos minutos antes de ver otro video.';

            return 'Al completar el video, Google valida la vista y los puntos llegan a tu cuenta.';
        }
    },
    created() {
        this.getData();
    },
    mounted() {
        window.addEventListener('valzum-rewards-refresh', this.nativeRewardRefresh);
    },
    beforeDestroy() {
        window.removeEventListener('valzum-rewards-refresh', this.nativeRewardRefresh);
    },
    methods: {
        getData() {
            this.loading = true;
            axios.get('/api/rewards').then(res => {
                this.rewards = Object.assign({}, this.rewards, res.data.rewards || {});
            }).catch(err => {
                this.$toast.error(this.errorMessage(err, 'No fue posible cargar tus recompensas.'), {icon: 'mdi-alert-circle'});
            }).then(() => {
                this.loading = false;
            });
        },
        redeem() {
            this.redeeming = true;
            axios.post('/api/rewards/redeem').then(res => {
                this.$toast.success(res.data.status, {icon: 'mdi-check-circle'});
                this.rewards = Object.assign({}, this.rewards, res.data.rewards || {});
            }).catch(err => {
                this.$toast.error(this.errorMessage(err, 'No fue posible canjear tus recompensas.'), {icon: 'mdi-alert-circle'});
            }).then(() => {
                this.redeeming = false;
            });
        },
        subscribe() {
            this.subscribing = true;
            axios.post('/api/rewards/subscribe').then(res => {
                this.$toast.success(res.data.status, {icon: 'mdi-check-circle'});
                this.rewards = Object.assign({}, this.rewards, res.data.rewards || {});
            }).catch(err => {
                this.$toast.error(this.errorMessage(err, 'No fue posible activar la suscripcion.'), {icon: 'mdi-alert-circle'});
            }).then(() => {
                this.subscribing = false;
            });
        },
        watchVideo() {
            if (!this.canRequestRewardVideo) return;

            const payload = this.rewardVideoPayload();
            let opened = false;

            this.watching = true;

            if (typeof window !== 'undefined' && window.ValzumRewards && typeof window.ValzumRewards.showRewardedAd === 'function') {
                window.ValzumRewards.showRewardedAd(JSON.stringify(payload));
                opened = true;
            } else if (
                typeof window !== 'undefined'
                && window.webkit
                && window.webkit.messageHandlers
                && window.webkit.messageHandlers.valzumRewards
            ) {
                window.webkit.messageHandlers.valzumRewards.postMessage(payload);
                opened = true;
            }

            if (!opened) {
                this.$toast.info('Abre esta seccion desde la app movil para ver videos recompensados.', {icon: 'mdi-cellphone-play'});
            }

            setTimeout(() => {
                this.watching = false;
                if (opened) this.getData();
            }, opened ? 4000 : 500);
        },
        rewardVideoPayload() {
            const admob = this.rewards.admob || {};

            return {
                user_id: admob.ssv_user_id || '',
                custom_data: admob.ssv_custom_data || '',
                android_ad_unit_id: admob.android_ad_unit_id || '',
                ios_ad_unit_id: admob.ios_ad_unit_id || '',
                reward_item: admob.reward_item || 'points',
                ssv_callback_url: admob.ssv_callback_url || ''
            };
        },
        nativeRewardRefresh() {
            this.getData();
        },
        errorMessage(err, fallback) {
            if (err.response && err.response.data && err.response.data.status) {
                return err.response.data.status;
            }

            return fallback;
        }
    }
}
</script>

<style scoped>
.reward-stat-card,
.reward-progress-card,
.reward-action-card,
.reward-history-card {
    border-radius: 8px;
}

.reward-stat-card {
    height: 100%;
}

.reward-stat-value {
    font-size: 1.5rem;
    line-height: 1.2;
}

.reward-stat-label,
.reward-stat-subtitle {
    font-size: .75rem;
    line-height: 1.25;
}

@media (max-width: 600px) {
    .rewards-container {
        padding-left: 10px;
        padding-right: 10px;
    }

    .rewards-main-col {
        padding-left: 0;
        padding-right: 0;
    }

    .rewards-shell > .v-card__title {
        font-size: .95rem;
        letter-spacing: 0;
        min-height: 38px;
    }

    .rewards-body {
        padding: 12px !important;
    }

    .reward-stats-row {
        margin-left: -3px;
        margin-right: -3px;
    }

    .reward-stats-row .col {
        padding-left: 3px;
        padding-right: 3px;
    }

    .reward-stat-card .v-card__text {
        padding: 10px 4px !important;
    }

    .reward-stat-value {
        font-size: 1rem;
        line-height: 1.2;
        overflow-wrap: anywhere;
    }

    .reward-stat-label,
    .reward-stat-subtitle {
        font-size: .66rem;
    }

    .reward-progress-card .v-card__text,
    .reward-action-card .v-card__text {
        padding: 14px !important;
    }

    .reward-history-card .v-card__title {
        font-size: .95rem !important;
        line-height: 1.2;
        min-height: 0;
        padding: 12px 14px 4px !important;
        word-break: normal;
    }

    .reward-history-card ::v-deep .v-data-table > .v-data-table__wrapper > table > tbody > tr > td,
    .reward-history-card ::v-deep .v-data-table > .v-data-table__wrapper > table > thead > tr > th {
        padding-left: 8px;
        padding-right: 8px;
        white-space: nowrap;
    }
}
</style>
